/** Périodes de comparaison — démo (euros entiers) et live (centimes). */

export type DateRange = { from: string; to: string };

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

export function toIso(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function parseIso(iso: string): Date {
  const [y, m, day] = iso.split("-").map(Number);
  return new Date(y, (m || 1) - 1, day || 1);
}

export function todayIso(): string {
  return toIso(new Date());
}

export function monthStartIso(d = new Date()): string {
  return toIso(new Date(d.getFullYear(), d.getMonth(), 1));
}

export function monthEndIso(d = new Date()): string {
  return toIso(new Date(d.getFullYear(), d.getMonth() + 1, 0));
}

const MONTHS = [
  "janvier", "février", "mars", "avril", "mai", "juin",
  "juillet", "août", "septembre", "octobre", "novembre", "décembre",
];

function dayLabel(day: number): string {
  return day === 1 ? "1er" : String(day);
}

/** Mois civil entier : 1er – 31 octobre. */
export function formatMonthRange(range: DateRange): string {
  const a = parseIso(range.from);
  const b = parseIso(range.to);
  const left = dayLabel(a.getDate());
  const right = dayLabel(b.getDate());
  const ma = MONTHS[a.getMonth()] ?? "";
  const mb = MONTHS[b.getMonth()] ?? "";
  if (a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear()) {
    return `${left} – ${right} ${mb}`;
  }
  return `${left} ${ma} – ${right} ${mb}`;
}

function addMonths(d: Date, delta: number): Date {
  const next = new Date(d.getFullYear(), d.getMonth() + delta, 1);
  const last = new Date(next.getFullYear(), next.getMonth() + 1, 0).getDate();
  next.setDate(Math.min(d.getDate(), last));
  return next;
}

export function inclusiveDays(from: string, to: string): number {
  const a = parseIso(from).getTime();
  const b = parseIso(to).getTime();
  return Math.max(1, Math.round((b - a) / 86400000) + 1);
}

/** Même fenêtre un mois plus tôt (1 → aujourd’hui vs 1 → même jour le mois d’avant). */
export function previousMonthToDate(range: DateRange): DateRange {
  return {
    from: toIso(addMonths(parseIso(range.from), -1)),
    to: toIso(addMonths(parseIso(range.to), -1)),
  };
}

/** Fenêtre précédente de la même durée, collée juste avant. */
export function previousEqualSpan(range: DateRange): DateRange {
  const days = inclusiveDays(range.from, range.to);
  const end = parseIso(range.from);
  end.setDate(end.getDate() - 1);
  const start = new Date(end);
  start.setDate(start.getDate() - (days - 1));
  return { from: toIso(start), to: toIso(end) };
}

const DATE_FMT = new Intl.DateTimeFormat("fr-FR", { day: "numeric", month: "short" });

export function formatRange(range: DateRange): string {
  return `${DATE_FMT.format(parseIso(range.from))} – ${DATE_FMT.format(parseIso(range.to))}`;
}

export function formatEuroHt(amount: number | null): string {
  if (amount === null) return "—";
  return `${new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(amount)} € HT`;
}

export function formatDeltaEuro(delta: number | null): string {
  if (delta === null) return "—";
  const sign = delta > 0 ? "+" : "";
  return `${sign}${new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(delta)} € HT`;
}

export function formatDeltaPct(pct: number | null): string {
  if (pct === null) return "—";
  const sign = pct > 0 ? "+" : "";
  return `${sign}${pct.toFixed(0)} %`;
}

export function formatEuroHtMoney(cents: number | null, money: (n: number) => string): string {
  if (cents === null) return "—";
  return `${money(cents)} HT`;
}

export function formatDeltaEuroMoney(delta: number | null, money: (n: number) => string): string {
  if (delta === null) return "—";
  const sign = delta > 0 ? "+" : "";
  return `${sign}${money(delta)} HT`;
}

export function deltaPct(current: number, previous: number): number | null {
  if (previous === 0) return current === 0 ? 0 : null;
  return ((current - previous) / previous) * 100;
}
